import React, { useState, useMemo } from 'react';
import Avatar from '../shared/Avatar';
import Badge from '../shared/Badge';

function Stars({ rating }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= rating ? '#F59E0B' : '#E5E7EB', fontSize: 14 }}>★</span>
      ))}
    </span>
  );
}

const FILTER_OPTS = [
  { key: 'all',      label: 'All'      },
  { key: 'positive', label: 'Positive' },
  { key: 'neutral',  label: 'Neutral'  },
  { key: 'negative', label: 'Negative' },
];

export default function FeedbackPage({ feedbackEntries, devIds, excludeDevs, accent }) {
  const ac = accent || '#CC1A00';
  const [ratingFilter, setRatingFilter] = useState('all');

  const filtered = useMemo(() => {
    return (feedbackEntries || [])
      .filter(f => excludeDevs ? !devIds.includes(f.user_id) : true)
      .filter(f => {
        if (ratingFilter === 'positive') return f.rating >= 4;
        if (ratingFilter === 'neutral')  return f.rating === 3;
        if (ratingFilter === 'negative') return f.rating <= 2;
        return true;
      });
  }, [feedbackEntries, devIds, excludeDevs, ratingFilter]);

  const avgRating = filtered.length
    ? (filtered.reduce((s, f) => s + f.rating, 0) / filtered.length).toFixed(1)
    : '—';

  const dist = [5, 4, 3, 2, 1].map(r => ({
    r,
    count: filtered.filter(f => f.rating === r).length,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 14 }}>
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', padding: '18px 20px' }}>
          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Ratings</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#111827' }}>{filtered.length}</div>
        </div>

        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', padding: '18px 20px' }}>
          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Rating</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
            {avgRating}
            <span style={{ color: '#F59E0B', fontSize: 24 }}>★</span>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', padding: '18px 20px' }}>
          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rating Distribution</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dist.map(item => {
              const pct      = filtered.length ? Math.round(item.count / filtered.length * 100) : 0;
              const barColor = item.r >= 4 ? '#15803D' : item.r === 3 ? '#D97706' : '#DC2626';
              return (
                <div key={item.r} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#374151', width: 18, textAlign: 'right', flexShrink: 0 }}>{item.r}★</span>
                  <div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: pct + '%', height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 11, color: '#9CA3AF', width: 20, flexShrink: 0 }}>{item.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', overflow: 'hidden' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, marginRight: 2 }}>Filter:</span>
          {FILTER_OPTS.map(f => {
            const active = ratingFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setRatingFilter(f.key)}
                style={{
                  padding: '5px 12px', borderRadius: 6, border: 'none',
                  background: active ? ac : '#F4F6F9',
                  color: active ? 'white' : '#374151',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {f.label}
              </button>
            );
          })}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF' }}>{filtered.length} entries</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['User', 'Rating', 'Comment', 'Date'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => {
                const is_dev = devIds.includes(f.user_id);
                return (
                  <tr key={f.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={f.email} size={28} bg={ac} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.email}</div>
                          {is_dev && <Badge text="DEV" color="#7C3AED" bg="#F5F3FF" size={9} />}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                      <Stars rating={f.rating} />
                    </td>
                    <td style={{ padding: '11px 16px', maxWidth: 320 }}>
                      <div style={{ fontSize: 12, color: f.comment ? '#374151' : '#D1D5DB', fontStyle: f.comment ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.comment || 'No comment left'}
                      </div>
                    </td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, color: '#9CA3AF' }}>{f.date}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              {(feedbackEntries || []).length === 0 ? 'No feedback has been submitted yet' : 'No feedback matches this filter'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
